import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Check, ArrowRight, ArrowLeft, User, GraduationCap, BookOpen, CreditCard, FileSignature, CheckCircle, Tag } from 'lucide-react';
import { PaymentMethodSelector, ZelleInstructionsPanel } from '../components/PaymentMethodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sigCanvas = useRef(null);
  
  const isReenroll = location.pathname.includes('reenroll');
  const tier = searchParams.get('tier') || 'normal';
  const isTrimestral = tier === 'trimestral';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [agreement, setAgreement] = useState({ content_es: '', content_en: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [methodDiscount, setMethodDiscount] = useState(0);
  const [zelleConfirmed, setZelleConfirmed] = useState(false);
  const [zelleData, setZelleData] = useState(null);
  
  // Re-enrollment specific state
  const [reenrollPlan, setReenrollPlan] = useState('full'); // 'full' or 'trimestral_3x'
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    parent_address: '',
    student_name: '',
    student_dob: '',
    student_grade: '',
    spanish_level: '',
    selected_course: '',
    payment_plan: isTrimestral ? 'trimestral_single' : 'full',
    pricing_tier: tier,
    sibling_discount: false,
    trimester: isTrimestral ? '' : null
  });

  const [savedSignatureData, setSavedSignatureData] = useState('');

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const agreementType = isTrimestral ? 'trimestral' : 'annual';
    fetchAgreement(agreementType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrimestral]);

  // Pre-fill parent info from authenticated user
  useEffect(() => {
    if (user && !formData.parent_email) {
      setFormData(prev => ({
        ...prev,
        parent_name: user.name || prev.parent_name,
        parent_email: user.email || prev.parent_email,
        parent_phone: user.phone || prev.parent_phone,
        parent_address: user.address || prev.parent_address
      }));
    }
  }, [user]);


  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchAgreement = async (type) => {
    try {
      const response = await axios.get(`${API}/enrollment-agreement?agreement_type=${type}`);
      setAgreement(response.data);
    } catch (error) {
      console.error('Error fetching agreement:', error);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.parent_name || !formData.parent_email || !formData.parent_phone || !formData.parent_address) {
          toast.error(language === 'es' ? 'Por favor complete todos los campos' : 'Please fill all fields');
          return false;
        }
        break;
      case 2:
        if (!formData.student_name || !formData.student_dob || !formData.student_grade || !formData.spanish_level) {
          toast.error(language === 'es' ? 'Por favor complete todos los campos' : 'Please fill all fields');
          return false;
        }
        break;
      case 3:
        if (!formData.selected_course) {
          toast.error(language === 'es' ? 'Por favor seleccione un curso' : 'Please select a course');
          return false;
        }
        if (isTrimestral && !formData.trimester) {
          toast.error(language === 'es' ? 'Por favor seleccione un trimestre' : 'Please select a trimester');
          return false;
        }
        break;
      case 4:
        if (!acceptedTerms) {
          toast.error(language === 'es' ? 'Debe aceptar las condiciones de matrícula' : 'You must accept the enrollment terms');
          return false;
        }
        if (!signerName || signerName.length < 3) {
          toast.error(language === 'es' ? 'Introduzca su nombre completo' : 'Please enter your full name');
          return false;
        }
        if (sigCanvas.current && sigCanvas.current.isEmpty()) {
          toast.error(language === 'es' ? 'Se requiere una firma' : 'Signature is required');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      // Save signature data before leaving step 4 (canvas will unmount)
      if (step === 4 && sigCanvas.current) {
        try {
          setSavedSignatureData(sigCanvas.current.toDataURL('image/png'));
        } catch (e) {
          console.error('Signature capture error:', e);
        }
      }
      setStep(prev => Math.min(prev + 1, 5));
    }
  };
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const clearSignature = () => sigCanvas.current?.clear();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const signatureData = savedSignatureData;

      // Create enrollment with payment_method
      const enrollPayload = {
        ...formData,
        pricing_tier: isReenroll && reenrollPlan === 'trimestral_3x' ? 'trimestral' : activeTier,
        payment_method: paymentMethod,
        ...(isReenroll && { is_reenrollment: true, reenroll_plan: reenrollPlan }),
        ...(isReenroll && reenrollPlan === 'trimestral_3x' && { payment_plan: 'trimestral_3x' }),
        ...(isReenroll && couponApplied && { coupon_code: couponCode.trim() })
      };
      const enrollmentResponse = await axios.post(`${API}/enrollments`, enrollPayload);
      const enrollmentId = enrollmentResponse.data.enrollment_id;
      const responseData = enrollmentResponse.data;

      // Sign contract
      if (enrollmentId) {
        await axios.post(`${API}/enrollments/${enrollmentId}/sign-contract`, {
          enrollment_id: enrollmentId,
          signer_name: signerName,
          signature_data: signatureData,
          accepted_terms: acceptedTerms
        });
      }

      // If Zelle: show confirmation + instructions (no Stripe redirect)
      if (paymentMethod === 'zelle') {
        setZelleData({
          enrollmentId,
          amount: responseData.installment_amount || responseData.total_amount,
          studentName: formData.student_name,
          zelleId: responseData.zelle_id,
        });
        setZelleConfirmed(true);
        setLoading(false);
        return;
      }

      // If Card/ACH: create Stripe checkout and redirect
      const checkoutResponse = await axios.post(`${API}/enrollments/${enrollmentId}/create-checkout`);
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
        return;
      }

      toast.success(t('enrollment.success'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  // Pricing logic - dynamic based on current date
  const prices = { early_bird: 1390, normal: 1495, trimestral: 620 };
  const getCurrentTier = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    // May 1 - June 30 → Early Bird, July 1 - August 31 → Normal
    if (month >= 5 && month <= 6) return 'early_bird';
    return 'normal';
  };
  const activeTier = isTrimestral ? 'trimestral' : getCurrentTier();

  // Auto-set pricing tier on mount
  useEffect(() => {
    if (!isTrimestral) {
      handleChange('pricing_tier', activeTier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTier, isTrimestral]);

  const getPrice = () => {
    // Re-enrollment with trimestral 3x plan
    if (isReenroll && reenrollPlan === 'trimestral_3x') {
      let price = 620; // per trimester
      if (formData.sibling_discount) price = price * 0.8;
      return price;
    }
    let price = prices[activeTier] || prices.normal;
    if (formData.sibling_discount) price = price * 0.8;
    // Apply coupon for re-enrollment pago único
    if (isReenroll && reenrollPlan === 'full' && couponApplied) {
      price = price - couponDiscount;
    }
    return price;
  };

  const getReenrollTotalDisplay = () => {
    if (reenrollPlan === 'trimestral_3x') {
      let perTrimester = 620;
      if (formData.sibling_discount) perTrimester = perTrimester * 0.8;
      return { total: perTrimester * 3, perPayment: perTrimester, payments: 3 };
    }
    let total = prices[activeTier] || prices.normal;
    if (formData.sibling_discount) total = total * 0.8;
    if (couponApplied) total = total - couponDiscount;
    return { total, perPayment: total, payments: 1 };
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode.trim(), plan_type: 'full' });
      if (res.data.valid) {
        setCouponApplied(true);
        setCouponDiscount(res.data.discount_amount);
        toast.success(language === 'es' ? `Cupón aplicado: -$${res.data.discount_amount}` : `Coupon applied: -$${res.data.discount_amount}`);
      }
    } catch (err) {
      setCouponApplied(false);
      setCouponDiscount(0);
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Cupón inválido' : 'Invalid coupon'));
    }
  };

  const getInstallmentAmount = () => {
    const total = getPrice();
    if (formData.payment_plan === 'two_installments') return (total / 2).toFixed(2);
    if (formData.payment_plan === 'monthly') return (total / 10).toFixed(2);
    return total.toFixed(2);
  };

  const steps = [
    { icon: User, label: t('enrollment.step1') },
    { icon: GraduationCap, label: t('enrollment.step2') },
    { icon: BookOpen, label: t('enrollment.step3') },
    { icon: FileSignature, label: t('enrollment.step4') },
    { icon: CreditCard, label: t('enrollment.step5') }
  ];

  const spanishLevels = [
    { value: 'beginner', label: language === 'es' ? 'Principiante' : 'Beginner' },
    { value: 'basic', label: language === 'es' ? 'Básico' : 'Basic' },
    { value: 'intermediate', label: language === 'es' ? 'Intermedio' : 'Intermediate' },
    { value: 'advanced', label: language === 'es' ? 'Avanzado' : 'Advanced' },
    { value: 'native', label: language === 'es' ? 'Nativo' : 'Native' }
  ];

  const grades = [
    { value: 'preschool', label: language === 'es' ? 'Preescolar' : 'Preschool' },
    { value: 'kindergarten', label: 'Kindergarten' },
    ...Array.from({length: 12}, (_, i) => ({
      value: `${i+1}`, label: language === 'es' ? `${i+1}° grado` : `${i+1}${['st','nd','rd'][i] || 'th'} Grade`
    }))
  ];

  const trimesters = [
    { value: 'T1', label: language === 'es' ? 'T1: Septiembre – Diciembre' : 'T1: September – December' },
    { value: 'T2', label: language === 'es' ? 'T2: Enero – Marzo' : 'T2: January – March' },
    { value: 'T3', label: language === 'es' ? 'T3: Abril – Junio' : 'T3: April – June' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <main className="pt-40 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F] mb-4" data-testid="enrollment-title">
              {t('enrollment.title')}
            </h1>
            {isTrimestral && (
              <span className="inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                {language === 'es' ? 'Plan Trimestral — $620/trimestre' : 'Trimester Plan — $620/trimester'}
              </span>
            )}
            {!isTrimestral && (
              <span className="inline-flex items-center gap-1 bg-[#34C759] text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                {activeTier === 'early_bird' ? 'Early Bird — $1,390' : 'Normal — $1,495'} / {language === 'es' ? 'año' : 'year'}
              </span>
            )}
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-12">
            {steps.map((s, index) => (
              <div key={index} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step > index + 1 ? 'bg-[#34C759] text-white' : 
                  step === index + 1 ? 'bg-[#E63946] text-white shadow-lg' : 'bg-black/5 text-[#86868B]'
                }`} data-testid={`step-indicator-${index + 1}`}>
                  {step > index + 1 ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 sm:w-14 h-0.5 mx-1 ${step > index + 1 ? 'bg-[#34C759]' : 'bg-black/10'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="card-apple p-8">
            {/* Step 1: Parent Info */}
            {step === 1 && (
              <div className="space-y-6" data-testid="enrollment-step-1">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-6">{t('enrollment.step1')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="parent_name">{t('enrollment.parentName')}</Label>
                    <Input id="parent_name" name="parent_name" value={formData.parent_name} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="parent-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent_email">{t('enrollment.parentEmail')}</Label>
                    <Input id="parent_email" name="parent_email" type="email" value={formData.parent_email} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="parent-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent_phone">{t('enrollment.parentPhone')}</Label>
                    <Input id="parent_phone" name="parent_phone" type="tel" value={formData.parent_phone} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="parent-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent_address">{t('enrollment.parentAddress')}</Label>
                    <Input id="parent_address" name="parent_address" value={formData.parent_address} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="parent-address-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Student Info */}
            {step === 2 && (
              <div className="space-y-6" data-testid="enrollment-step-2">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-6">{t('enrollment.step2')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t('enrollment.studentName')}</Label>
                    <Input name="student_name" value={formData.student_name} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="student-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.studentDob')}</Label>
                    <Input name="student_dob" type="date" value={formData.student_dob} onChange={handleInputChange} className="py-6 rounded-xl" data-testid="student-dob-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.studentGrade')}</Label>
                    <Select value={formData.student_grade} onValueChange={(v) => handleChange('student_grade', v)}>
                      <SelectTrigger className="py-6 rounded-xl" data-testid="student-grade-select"><SelectValue placeholder={language === 'es' ? 'Seleccionar' : 'Select'} /></SelectTrigger>
                      <SelectContent>{grades.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.spanishLevel')}</Label>
                    <Select value={formData.spanish_level} onValueChange={(v) => handleChange('spanish_level', v)}>
                      <SelectTrigger className="py-6 rounded-xl" data-testid="spanish-level-select"><SelectValue placeholder={language === 'es' ? 'Seleccionar' : 'Select'} /></SelectTrigger>
                      <SelectContent>{spanishLevels.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Course Selection + Trimester */}
            {step === 3 && (
              <div className="space-y-6" data-testid="enrollment-step-3">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-6">{t('enrollment.step3')}</h2>
                
                {/* Trimester selection for trimestral plans */}
                {isTrimestral && (
                  <div className="space-y-3 mb-6">
                    <Label className="text-base font-semibold">
                      {language === 'es' ? 'Seleccionar trimestre' : 'Select trimester'}
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {trimesters.map(tri => (
                        <div
                          key={tri.value}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                            formData.trimester === tri.value
                              ? 'border-[#007AFF] bg-[#007AFF]/5'
                              : 'border-black/5 hover:border-black/20'
                          }`}
                          onClick={() => handleChange('trimester', tri.value)}
                          data-testid={`trimester-${tri.value}`}
                        >
                          <p className="font-semibold text-[#1D1D1F]">{tri.value}</p>
                          <p className="text-xs text-[#86868B] mt-1">{tri.label.split(': ')[1]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {courses.map(course => (
                    <div
                      key={course.course_id}
                      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                        formData.selected_course === course.course_id
                          ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/5 hover:border-black/20'
                      }`}
                      onClick={() => handleChange('selected_course', course.course_id)}
                      data-testid={`course-option-${course.course_id}`}
                    >
                      <h3 className="font-semibold text-[#1D1D1F] mb-1">{language === 'es' ? course.name : (course.name_en || course.name)}</h3>
                      <p className="text-sm text-[#86868B] mb-2">{course.age_range}</p>
                      <p className="text-sm text-[#86868B]">{language === 'es' ? course.description : (course.description_en || course.description)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Contract Agreement & Signature */}
            {step === 4 && (
              <div className="space-y-6" data-testid="enrollment-step-4">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">{t('enrollment.contractTitle')}</h2>
                <p className="text-sm text-[#86868B] mb-4">
                  {isTrimestral 
                    ? (language === 'es' ? 'Contrato trimestral — cubre únicamente el trimestre seleccionado.' : 'Trimester agreement — covers only the selected trimester.')
                    : (language === 'es' ? 'Contrato anual — compromiso para el año escolar completo.' : 'Annual agreement — commitment for the full school year.')}
                </p>
                
                <div className="border border-black/10 rounded-2xl overflow-hidden">
                  <div className="p-6 max-h-[350px] overflow-y-auto bg-white text-sm text-[#1D1D1F] leading-relaxed whitespace-pre-line" data-testid="contract-text">
                    {language === 'es' ? agreement.content_es : agreement.content_en}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#F5F5F7] rounded-xl">
                  <Checkbox id="accept_terms" checked={acceptedTerms} onCheckedChange={(c) => setAcceptedTerms(c)} data-testid="accept-terms-checkbox" />
                  <Label htmlFor="accept_terms" className="cursor-pointer text-sm leading-relaxed">{t('enrollment.contractAccept')}</Label>
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.signatureName')}</Label>
                  <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder={language === 'es' ? 'Nombre completo' : 'Full name'} className="py-6 rounded-xl" data-testid="signer-name-input" />
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.signatureCanvas')}</Label>
                  <div className="border-2 border-dashed border-black/15 rounded-2xl bg-white overflow-hidden relative">
                    <SignatureCanvas ref={sigCanvas} penColor="#1D1D1F" canvasProps={{ width: 660, height: 180, className: 'w-full h-[180px]', 'data-testid': 'signature-canvas' }} />
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-[#86868B]/50 pointer-events-none">
                      {language === 'es' ? 'Dibuje su firma aquí' : 'Draw your signature here'}
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={clearSignature} className="rounded-lg text-xs" data-testid="clear-signature-btn">
                    {t('enrollment.signatureClear')}
                  </Button>
                </div>

                <div className="text-sm text-[#86868B]">
                  {language === 'es' ? 'Fecha: ' : 'Date: '}
                  {new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            )}

            {/* Step 5: Payment */}
            {step === 5 && (
              <div className="space-y-6" data-testid="enrollment-step-5">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-6">{t('enrollment.step5')}</h2>
                
                {/* RE-ENROLLMENT: 2 options only */}
                {isReenroll && !isTrimestral ? (
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">{language === 'es' ? 'Plan de pago' : 'Payment plan'}</Label>
                      
                      {/* Option 1: Pago único */}
                      <div
                        className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                          reenrollPlan === 'full' ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/5 hover:border-black/20'
                        }`}
                        onClick={() => { setReenrollPlan('full'); handleChange('payment_plan', 'full'); setCouponApplied(false); setCouponDiscount(0); setCouponCode(''); }}
                        data-testid="reenroll-plan-full"
                      >
                        <p className="font-semibold text-[#1D1D1F]">{language === 'es' ? 'Pago único (100%)' : 'Full payment (100%)'}</p>
                        <p className="text-sm text-[#86868B] mt-1">${(prices[activeTier] || 1495).toLocaleString()} {language === 'es' ? 'al momento' : 'at enrollment'}</p>
                        {reenrollPlan === 'full' && (
                          <p className="text-xs text-[#34C759] mt-2 font-medium">{language === 'es' ? 'Cupón disponible: -$100 ancien élève' : 'Coupon available: -$100 returning student'}</p>
                        )}
                      </div>

                      {/* Option 2: Trimestral 3x */}
                      <div
                        className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                          reenrollPlan === 'trimestral_3x' ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-black/5 hover:border-black/20'
                        }`}
                        onClick={() => { setReenrollPlan('trimestral_3x'); handleChange('payment_plan', 'trimestral_3x'); setCouponApplied(false); setCouponDiscount(0); setCouponCode(''); }}
                        data-testid="reenroll-plan-trimestral"
                      >
                        <p className="font-semibold text-[#1D1D1F]">{language === 'es' ? 'Pago trimestral (3 cuotas)' : 'Trimester payment (3 installments)'}</p>
                        <p className="text-sm text-[#86868B] mt-1">3 × $620 = $1,860 {language === 'es' ? 'total' : 'total'}</p>
                        <p className="text-xs text-[#86868B] mt-1">{language === 'es' ? 'T1: septiembre · T2: diciembre · T3: marzo' : 'T1: September · T2: December · T3: March'}</p>
                      </div>
                    </div>

                    {/* Coupon field — only for pago único */}
                    {reenrollPlan === 'full' && (
                      <div className="space-y-2" data-testid="coupon-section">
                        <Label className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-[#E63946]" />
                          {language === 'es' ? 'Código de descuento ancien élève' : 'Returning student discount code'}
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            value={couponCode}
                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponApplied(false); setCouponDiscount(0); }}
                            placeholder={language === 'es' ? 'Ej: ARCE100' : 'e.g. ARCE100'}
                            className="py-5 rounded-xl uppercase font-mono"
                            data-testid="coupon-input"
                          />
                          <Button
                            type="button"
                            onClick={applyCoupon}
                            disabled={!couponCode.trim() || couponApplied}
                            className={`rounded-xl px-6 ${couponApplied ? 'bg-[#34C759] hover:bg-[#34C759]' : 'bg-[#E63946] hover:bg-[#D62828]'}`}
                            data-testid="apply-coupon-btn"
                          >
                            {couponApplied ? <Check className="h-4 w-4" /> : (language === 'es' ? 'Aplicar' : 'Apply')}
                          </Button>
                        </div>
                        {couponApplied && (
                          <p className="text-sm text-[#34C759] font-medium" data-testid="coupon-success">
                            {language === 'es' ? `Descuento ancien élève aplicado: -$${couponDiscount}` : `Returning student discount applied: -$${couponDiscount}`}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Sibling Discount */}
                    <div className="flex items-center gap-3">
                      <Checkbox id="sibling_discount" checked={formData.sibling_discount} onCheckedChange={(c) => handleChange('sibling_discount', c)} data-testid="sibling-discount-checkbox" />
                      <Label htmlFor="sibling_discount" className="cursor-pointer">{t('enrollment.siblingDiscount')} (-20%)</Label>
                    </div>

                    {/* Summary */}
                    <div className="p-6 bg-[#F5F5F7] rounded-2xl space-y-3">
                      {reenrollPlan === 'full' ? (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-[#86868B]">{language === 'es' ? 'Tarifa anual' : 'Annual rate'}</span>
                            <span className="text-lg text-[#1D1D1F]">${(prices[activeTier] || 1495).toLocaleString()}</span>
                          </div>
                          {formData.sibling_discount && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-[#34C759]">{language === 'es' ? 'Descuento hermano (-20%)' : 'Sibling discount (-20%)'}</span>
                              <span className="text-[#34C759]">-${Math.round((prices[activeTier] || 1495) * 0.2)}</span>
                            </div>
                          )}
                          {couponApplied && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-[#34C759]">{language === 'es' ? 'Cupón ARCE100' : 'Coupon ARCE100'}</span>
                              <span className="text-[#34C759]">-${couponDiscount}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center pt-2 border-t border-black/10">
                            <span className="font-semibold text-[#1D1D1F]">Total</span>
                            <span className="text-3xl font-bold text-[#1D1D1F]" data-testid="enrollment-total">${getReenrollTotalDisplay().total.toLocaleString()}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-[#86868B]">{language === 'es' ? 'Por trimestre' : 'Per trimester'}</span>
                            <span className="text-2xl font-bold text-[#1D1D1F]">${getReenrollTotalDisplay().perPayment}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-[#86868B]">{language === 'es' ? 'Total año (3 trimestres)' : 'Total year (3 trimesters)'}</span>
                            <span className="text-[#1D1D1F] font-medium">${getReenrollTotalDisplay().total.toLocaleString()}</span>
                          </div>
                          {formData.sibling_discount && (
                            <p className="text-sm text-[#34C759]">{language === 'es' ? 'Descuento de hermano aplicado (-20%)' : 'Sibling discount applied (-20%)'}</p>
                          )}
                          <div className="p-3 bg-[#007AFF]/10 rounded-lg mt-2">
                            <p className="text-xs text-[#007AFF]">
                              {language === 'es'
                                ? 'T1: pago al inscribirse · T2: recordatorio el 1 dic 2026 · T3: recordatorio el 1 mar 2027'
                                : 'T1: pay at enrollment · T2: reminder Dec 1, 2026 · T3: reminder Mar 1, 2027'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {reenrollPlan === 'full' && !couponApplied && (
                      <div className="p-4 bg-[#FFF8E1] rounded-xl text-sm text-[#1D1D1F]">
                        <strong>{language === 'es' ? 'Nota:' : 'Note:'}</strong>{' '}
                        {language === 'es'
                          ? 'Como antiguo alumno, tienes acceso a un cupón de descuento de $100. Solicítalo en laura@colegioarce.com.'
                          : 'As a returning student, you qualify for a $100 discount coupon. Request it at laura@colegioarce.com.'}
                      </div>
                    )}
                  </div>
                ) : isTrimestral ? (
                  <div className="space-y-6">
                    <div className="p-6 bg-[#007AFF]/5 border-2 border-[#007AFF] rounded-2xl">
                      <p className="font-semibold text-[#1D1D1F] text-lg mb-2">
                        {language === 'es' ? 'Plan Trimestral' : 'Trimester Plan'}
                      </p>
                      <p className="text-sm text-[#86868B] mb-3">
                        {trimesters.find(t => t.value === formData.trimester)?.label || formData.trimester}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-[#1D1D1F]">${getPrice().toLocaleString()}</span>
                        <span className="text-[#86868B]">{language === 'es' ? 'pago único' : 'one-time payment'}</span>
                      </div>
                    </div>

                    {/* Sibling Discount */}
                    <div className="flex items-center gap-3">
                      <Checkbox id="sibling_discount" checked={formData.sibling_discount} onCheckedChange={(c) => handleChange('sibling_discount', c)} data-testid="sibling-discount-checkbox" />
                      <Label htmlFor="sibling_discount" className="cursor-pointer">{t('enrollment.siblingDiscount')} (-20%)</Label>
                    </div>

                    {formData.sibling_discount && (
                      <p className="text-sm text-[#34C759]">{language === 'es' ? 'Descuento de hermano aplicado (-20%)' : 'Sibling discount applied (-20%)'}</p>
                    )}

                    <div className="p-4 bg-[#FFF8E1] rounded-xl text-sm text-[#1D1D1F]">
                      <strong>{language === 'es' ? 'Nota:' : 'Note:'}</strong>{' '}
                      {language === 'es' 
                        ? 'El plan trimestral cuesta $620 × 3 = $1,860/año. Los planes anuales ofrecen hasta $465 de ahorro. Puede cambiar a un plan anual en cualquier momento.'
                        : 'The trimester plan costs $620 × 3 = $1,860/year. Annual plans save up to $465. You can upgrade to an annual plan at any time.'}
                    </div>
                  </div>
                ) : (
                  /* For annual: auto pricing tier + payment plan options */
                  <div className="space-y-6">
                    {/* Active pricing tier (auto-selected based on date) */}
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Tarifa' : 'Pricing Tier'}</Label>
                      <div className="p-4 rounded-xl border-2 border-[#E63946] bg-[#E63946]/5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-[#1D1D1F]">
                              {activeTier === 'early_bird' ? t('pricing.earlyBird') : t('pricing.normal')}
                            </p>
                            <p className="text-xs text-[#86868B] mt-1">
                              {activeTier === 'early_bird' ? t('pricing.earlyBirdPeriod') : t('pricing.normalPeriod')}
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-[#E63946]">
                            ${prices[activeTier].toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Plan */}
                    <div className="space-y-3">
                      <Label>{t('enrollment.paymentOption')}</Label>
                      {[
                        { value: 'full', label: t('enrollment.fullPayment'), desc: language === 'es' ? `$${getPrice().toLocaleString()} al momento` : `$${getPrice().toLocaleString()} at enrollment` },
                        { value: 'two_installments', label: t('enrollment.twoInstallments'), desc: language === 'es' ? `2 × $${(getPrice()/2).toFixed(2)}` : `2 × $${(getPrice()/2).toFixed(2)}` },
                        { value: 'monthly', label: t('enrollment.monthlyPayment'), desc: language === 'es' ? `10 × $${(getPrice()/10).toFixed(2)}/mes` : `10 × $${(getPrice()/10).toFixed(2)}/mo` }
                      ].map(plan => (
                        <div key={plan.value}
                          className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.payment_plan === plan.value ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/5 hover:border-black/20'
                          }`}
                          onClick={() => handleChange('payment_plan', plan.value)}
                          data-testid={`payment-plan-${plan.value}`}
                        >
                          <p className="font-semibold text-[#1D1D1F]">{plan.label}</p>
                          <p className="text-sm text-[#86868B] mt-1">{plan.desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* Sibling Discount */}
                    <div className="flex items-center gap-3">
                      <Checkbox id="sibling_discount" checked={formData.sibling_discount} onCheckedChange={(c) => handleChange('sibling_discount', c)} data-testid="sibling-discount-checkbox" />
                      <Label htmlFor="sibling_discount" className="cursor-pointer">{t('enrollment.siblingDiscount')} (-20%)</Label>
                    </div>

                    {/* Summary */}
                    <div className="p-6 bg-[#F5F5F7] rounded-2xl space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#86868B]">Total</span>
                        <span className="text-3xl font-bold text-[#1D1D1F]" data-testid="enrollment-total">${getPrice().toLocaleString()}</span>
                      </div>
                      {formData.payment_plan !== 'full' && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-[#86868B]">{language === 'es' ? 'Por cuota' : 'Per installment'}</span>
                          <span className="font-semibold text-[#1D1D1F]" data-testid="installment-amount">${getInstallmentAmount()}</span>
                        </div>
                      )}
                      {formData.sibling_discount && (
                        <p className="text-sm text-[#34C759]">{language === 'es' ? 'Descuento de hermano aplicado (-20%)' : 'Sibling discount applied (-20%)'}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Contract signed badge */}
                <div className="flex items-center gap-2 p-3 bg-[#34C759]/10 rounded-xl text-sm text-[#34C759] font-medium">
                  <Check className="h-4 w-4" />
                  {language === 'es' ? 'Contrato firmado por: ' : 'Contract signed by: '}{signerName}
                </div>

                {/* Payment Method Selection */}
                <PaymentMethodSelector
                  amount={getPrice()}
                  language={language}
                  onMethodChange={(method, discount) => {
                    setPaymentMethod(method);
                    setMethodDiscount(discount);
                  }}
                  showComparison={true}
                />

                <div className="p-4 bg-[#007AFF]/10 rounded-xl">
                  <p className="text-sm text-[#1D1D1F]">
                    <strong>{language === 'es' ? 'Siguiente paso:' : 'Next step:'}</strong>{' '}
                    {paymentMethod === 'zelle' 
                      ? (language === 'es' 
                          ? 'Al enviar, recibirás las instrucciones de pago Zelle con el código QR.' 
                          : 'Upon submission, you will receive Zelle payment instructions with QR code.')
                      : (language === 'es' 
                          ? 'Al enviar, serás redirigido a Stripe para completar el pago de forma segura.' 
                          : 'Upon submission, you will be redirected to Stripe to securely complete payment.')}
                  </p>
                </div>
              </div>
            )}

            {/* Zelle Confirmation Panel */}
            {zelleConfirmed && zelleData && (
              <div className="space-y-6" data-testid="zelle-confirmation-panel">
                <div className="p-6 bg-[#34C759]/10 rounded-2xl text-center">
                  <CheckCircle className="h-12 w-12 text-[#34C759] mx-auto mb-3" />
                  <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                    {language === 'es' ? 'Inscripción registrada' : 'Enrollment registered'}
                  </h3>
                  <p className="text-[#6E6E73]">
                    {language === 'es' 
                      ? 'Tu inscripción ha sido registrada. Completa el pago por Zelle para activarla.'
                      : 'Your enrollment has been registered. Complete the Zelle payment to activate it.'}
                  </p>
                </div>

                <ZelleInstructionsPanel 
                  amount={zelleData.amount} 
                  studentNames={zelleData.studentName} 
                  language={language} 
                />

                <div className="p-4 bg-[#F5F5F7] rounded-xl text-center">
                  <p className="text-sm text-[#86868B]">
                    {language === 'es' ? 'Referencia:' : 'Reference:'}{' '}
                    <span className="font-mono font-semibold text-[#1D1D1F]">{zelleData.enrollmentId}</span>
                  </p>
                </div>

                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full rounded-xl py-6 bg-[#1D1D1F] hover:bg-[#333] text-white font-medium"
                  data-testid="zelle-done-btn"
                >
                  {language === 'es' ? 'Volver al inicio' : 'Back to home'}
                </Button>
              </div>
            )}

            {/* Navigation */}
            {!zelleConfirmed && (
            <div className="flex justify-between mt-8 pt-6 border-t border-black/5">
              <Button type="button" variant="outline" onClick={prevStep} disabled={step === 1} className="rounded-xl px-6" data-testid="enrollment-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />{t('enrollment.back')}
              </Button>
              {step < 5 ? (
                <Button type="button" onClick={nextStep} className="rounded-xl px-6 bg-[#E63946] hover:bg-[#D62828]" data-testid="enrollment-next-btn">
                  {t('enrollment.next')}<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button type="button" onClick={handleSubmit} disabled={loading} className="rounded-xl px-6 bg-[#E63946] hover:bg-[#D62828]" data-testid="enrollment-submit-btn">
                  {loading ? (language === 'es' ? 'Enviando...' : 'Submitting...') : t('enrollment.submit')}
                </Button>
              )}
            </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
